import {
  CONTEXT_ALL,
  CONTEXT_PERSONAL,
  CONTEXT_PROFESSIONAL,
  FACET_CONTEXTS,
  facetDefaultsForContext,
  isDiscoveryEnabled,
  normalizeRolodexEntry,
  normalizeRolodexEntries,
  normalizeRolodexFacet,
  originForTarget,
  personKeyOf,
  projectEntriesToContext,
  projectEntryToContext,
  resolveOriginTarget,
} from "./rolodexContext";
import { closenessFor, strengthFromCloseness } from "./connectionDetails";

function dateInput(value) {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

export function blankFacet(context = CONTEXT_PROFESSIONAL) {
  const defaults = facetDefaultsForContext(context);
  return {
    relationshipType: defaults.relationship_type,
    strength: defaults.connection_strength,
    closeness: defaults.closeness,
    whereMet: defaults.where_met,
    privateNote: defaults.private_note,
    lastInteracted: "",
    discoveryEnabled: defaults.discovery_enabled,
    branch: { parentUid: null, parentName: "", mode: null },
  };
}

export function facetFormFromStored(
  facet,
  context = CONTEXT_PROFESSIONAL,
  entries = []
) {
  if (!facet) return null;
  const normalized = normalizeRolodexFacet(facet, context);
  const origin = normalized.origin;
  const resolvedOrigin = resolveOriginTarget(origin, entries);
  return {
    relationshipType: normalized.relationship_type,
    strength: normalized.connection_strength,
    closeness: closenessFor(normalized),
    whereMet: normalized.where_met,
    privateNote: normalized.private_note,
    lastInteracted: dateInput(normalized.last_interacted_at),
    discoveryEnabled: isDiscoveryEnabled(normalized, context),
    branch: {
      parentUid: resolvedOrigin.personKey,
      parentName: origin?.name || origin?.parent_name || "",
      mode: origin?.mode || null,
    },
  };
}

export function storedFacetFromForm(
  form,
  entries = [],
  context = CONTEXT_PERSONAL
) {
  if (!form) return null;
  const defaults = facetDefaultsForContext(context);
  const origin = originForTarget(form.branch?.parentUid, entries, {
    name: form.branch?.parentName || "",
    mode: form.branch?.mode || "direct",
  });
  // Closeness is the finer-grained value, so when the form carries one it
  // decides the stored strength that the rest of the app reads.
  const closeness = closenessFor({
    closeness: form.closeness,
    connection_strength: form.strength || defaults.connection_strength,
  });
  return {
    relationship_type: form.relationshipType || defaults.relationship_type,
    connection_strength: form.closeness
      ? strengthFromCloseness(closeness)
      : form.strength || defaults.connection_strength,
    closeness,
    where_met: form.whereMet || defaults.where_met,
    private_note: form.privateNote || defaults.private_note,
    last_interacted_at: form.lastInteracted ? new Date(`${form.lastInteracted}T12:00:00`) : null,
    discovery_enabled: form.discoveryEnabled === true,
    ...(origin ? { origin } : {}),
  };
}

/** Flatten an entry into the legacy display shape consumed by list/detail UI. */
export function entryForContext(
  entry,
  context = CONTEXT_ALL,
  allEntries = []
) {
  return projectEntryToContext(entry, context, allEntries);
}

export function entriesForContext(entries, context = CONTEXT_ALL) {
  return projectEntriesToContext(entries, context);
}

export function formsFromEntry(entry, entries = []) {
  const normalized = normalizeRolodexEntry(entry);
  return {
    [CONTEXT_PROFESSIONAL]: facetFormFromStored(
      normalized?.facets?.[CONTEXT_PROFESSIONAL],
      CONTEXT_PROFESSIONAL,
      entries
    ),
    [CONTEXT_PERSONAL]: facetFormFromStored(
      normalized?.facets?.[CONTEXT_PERSONAL],
      CONTEXT_PERSONAL,
      entries
    ),
  };
}

export function storedFacetsFromForms(forms, entries = []) {
  return {
    ...(forms?.[CONTEXT_PROFESSIONAL]
      ? {
          [CONTEXT_PROFESSIONAL]: storedFacetFromForm(
            forms[CONTEXT_PROFESSIONAL],
            entries,
            CONTEXT_PROFESSIONAL
          ),
        }
      : {}),
    ...(forms?.[CONTEXT_PERSONAL]
      ? {
          [CONTEXT_PERSONAL]: storedFacetFromForm(
            forms[CONTEXT_PERSONAL],
            entries,
            CONTEXT_PERSONAL
          ),
        }
      : {}),
  };
}

function parentEntryId(origin, entries) {
  return resolveOriginTarget(origin, entries).entryId;
}

/** Node ids that cannot parent `entryId` without creating a context cycle. */
export function descendantNodeIds(entries, entryId, context) {
  const normalizedEntries = normalizeRolodexEntries(entries);
  const children = new Map();
  normalizedEntries.forEach((entry) => {
    const parentId = parentEntryId(
      entry.facets?.[context]?.origin,
      normalizedEntries
    );
    if (!parentId) return;
    if (!children.has(parentId)) children.set(parentId, []);
    children.get(parentId).push(entry);
  });

  const excluded = new Set();
  const pending = [entryId];
  while (pending.length) {
    const parentId = pending.pop();
    (children.get(parentId) || []).forEach((child) => {
      if (excluded.has(child.id)) return;
      excluded.add(child.id);
      const childPersonKey = personKeyOf(child);
      if (childPersonKey) excluded.add(childPersonKey);
      pending.push(child.id);
    });
  }
  return excluded;
}

export { FACET_CONTEXTS };
